import { addPageAction } from '../util/new_relic';

const SUSI_LIGHT_CONTAINER_CLASS = 'af-susi-light-rightside-container';
const SUSI_LIGHT_CONSENT_PROFILE = 'adobe-id-sign-up';
const SUSI_LIGHT_RESPONSE_TYPE = 'code';
const SUSI_LIGHT_VARIANT = 'standard';

class SUSILight extends HTMLElement {
  constructor() {
    super();
    this._sentryElement = null;
    this._allowedDomains = ['adobelogin.com', 'adobe.com'];
  }

  async connectedCallback() {
    this.container = document.querySelector(`.${SUSI_LIGHT_CONTAINER_CLASS}`);
    if (!this.container) {
      return;
    }

    const clientId = this.getAttribute('client_id');
    const locale = this.getAttribute('locale');
    const loginURL = this.getAttribute('login_url');
    const scope = this.getAttribute('scope');
    const env = this.getAttribute('env');

    this.authParams = {
      client_id: clientId,
      dt: false, // toggles the dark theme setting
      locale: locale,
      redirect_uri: loginURL,
      response_type: SUSI_LIGHT_RESPONSE_TYPE,
      scope: scope
    };

    this.config = {
      consentProfile: SUSI_LIGHT_CONSENT_PROFILE
    };

    this.isPopup = false;
    this.variant = SUSI_LIGHT_VARIANT;
    this.isStageEnvironment = env !== 'prod';

    try {
      await this._loadScript();
      this._createSentryElement();
    } catch (error) {
      if (this.isStageEnvironment) {
        console.error('There was an error initializing SUSILight:', error);
      } else {
        addPageAction('susi-light-error', {
          error: 'Error initializing SUSILight',
          message: error.message
        });
      }
    }
  }

  disconnectedCallback() {
    if (this._sentryElement) {
      this._sentryElement.removeEventListener('on-analytics', this._onAnalytics);
      this._sentryElement.removeEventListener('on-error', this._onError);
      this._sentryElement.removeEventListener('redirect', this._onRedirect);
    }
  }

  _loadScript = () => {
    const scriptUrl = this.getAttribute('js');

    if (!scriptUrl) {
      return Promise.reject(new Error('No script URL provided'));
    }

    if (customElements.get('susi-sentry-light')) {
      return Promise.resolve();
    }

    const script = document.createElement('script');
    script.src = scriptUrl;

    return new Promise((resolve, reject) => {
      script.addEventListener('load', resolve);
      script.addEventListener('error', reject);
      document.head.appendChild(script);
    });
  };

  _createSentryElement = () => {
    if (this._sentryElement) {
      return;
    }

    let sentry = this.container.querySelector('susi-sentry-light');
    if (!sentry) {
      sentry = document.createElement('susi-sentry-light');
    }

    // Set properties using both attribute and property assignment for full compatibility
    // when setting values on custom properties
    sentry.setAttribute('auth-params', JSON.stringify(this.authParams));
    sentry.setAttribute('config', JSON.stringify(this.config));
    sentry.setAttribute('stage', this.isStageEnvironment);
    sentry.setAttribute('variant', this.variant);

    sentry.authParams = this.authParams;
    sentry.config = this.config;
    sentry.popup = this.isPopup;
    sentry.stage = this.isStageEnvironment;
    sentry.variant = this.variant;

    sentry.addEventListener('on-analytics', this._onAnalytics);
    sentry.addEventListener('on-error', this._onError);
    sentry.addEventListener('redirect', this._onRedirect);

    this._sentryElement = sentry;
    this.container.appendChild(sentry);
  };

  /**
   * The on-analytics event is triggered when a user completes an action.
   * The e.detail field contains the name of the corresponding analytics event.
   * This event is only supported by the susi-sentry-light web component and can
   * serve as a substitute for the on-provider-clicked event when needed.
   *
   * For example, if the user clicks the Google social login option, the
   * e.detail field would contain Account:IMS-Light:ContinueWithGoogleClick.
   * @param {*} event
   */
  _onAnalytics = (event) => {
    const actionName = event.detail;
    addPageAction(`typekit.susi-light.${actionName}`);
  };

  _onRedirect = (event) => {
    const redirectLink = event.detail;

    try {
      const url = new URL(redirectLink);
      const isValidAdobeLogin = this._allowedDomains.some(domain =>
        url.hostname.endsWith(domain)
      ) && url.protocol === 'https:';

      if (isValidAdobeLogin) {
        window.location.replace(redirectLink);
      } else {
        addPageAction('susi-light-redirect-error', {
          error: 'Invalid redirect URL: Not a valid Adobe login URL',
          url: redirectLink
        });
      }
    } catch (error) {
      addPageAction('susi-light-redirect-error', {
        error: 'Invalid redirect URL: Malformed URL',
        message: error.message
      });
    }
  };

  _onError = (event) => {
    addPageAction('susi-light-error', {
      error: event.detail
    });
  };

  _updateAuthParams = (newParams) => {
    if (!this._sentryElement) {
      return;
    }

    const updatedParams = {
      ...this.authParams,
      ...newParams
    };

    // Update both the property and attribute for full compatibility
    this._sentryElement.authParams = updatedParams;
    this._sentryElement.setAttribute('authParams', JSON.stringify(updatedParams));

    this.authParams = updatedParams;
  };

  updateRedirectUri = (redirectUri) => {
    this._updateAuthParams({ redirect_uri: redirectUri });
  };
}

customElements.define('af-susi-light', SUSILight);
